import {
  Flex,
  Heading,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { useGetDeliverableArtifactInstanceQuery } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableArtifactInstance.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { getHumanReadableDateRange } from '~/packages/common-frontend/utils';
import {
  MonthlyStatementDailyAggregateTable,
  MonthlyStatementSummaryBalance,
} from '~/packages/monthlyStatement';

type DigitalMonthlyReportMonthlyStatements = {
  monthlyStatementArtifactInstanceId: string;
};

export const DigitalMonthlyReportMonthlyStatement = ({
  monthlyStatementArtifactInstanceId,
}: DigitalMonthlyReportMonthlyStatements) => {
  const { data, loading } = useGetDeliverableArtifactInstanceQuery({
    variables: {
      artifactInstanceId: monthlyStatementArtifactInstanceId,
    },
  });

  if (!data || loading) {
    // @jendevelops MSR-TODO: add skeleton loader
    return <Skeleton h="600px" />;
  }

  const { completedObject } = data.deliverableArtifactInstance;

  finleyAssert(
    completedObject,
    `Expected completed object to exist for submitted monthly statement artifact instance id [${monthlyStatementArtifactInstanceId}]`,
  );

  const dailyAggregateData = completedObject.object;

  return (
    <VStack
      backgroundColor="white"
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      padding="40px"
      spacing="40px"
      alignItems="flex-start"
    >
      <Heading as="h2" fontSize="24px" mb="40px">
        Monthly Statements
      </Heading>

      <Flex
        backgroundColor={colors.white}
        w="100%"
        p="24px 16px"
        borderRadius="8px"
        border={`1px solid ${colors.gray[100]}`}
      >
        <HStack w="50%" spacing="16px">
          <Text fontWeight="bold">Periods</Text>
        </HStack>
        <Text fontWeight="500" flexGrow={1} ml="32px">
          {getHumanReadableDateRange({
            startDate: dailyAggregateData.period.startDate,
            endDate: dailyAggregateData.period.endDate,
          })}
        </Text>
      </Flex>

      <VStack
        backgroundColor={colors.white}
        w="100%"
        p="24px"
        borderRadius="8px"
        border={`1px solid ${colors.gray[100]}`}
      >
        <MonthlyStatementSummaryBalance
          totalBalance={dailyAggregateData.summary.totalBalance}
          balances={dailyAggregateData.summary.balances}
        />
      </VStack>

      <Flex w="100%" alignItems="center" justify="space-between">
        <Heading fontSize="20px">Daily Totals</Heading>
        {/* MSR-TODO hook up daily aggregation download */}
      </Flex>

      <MonthlyStatementDailyAggregateTable
        dailyAggregateData={dailyAggregateData}
      />
    </VStack>
  );
};
